import {
  ArrowRightOutlined,
  AuditOutlined,
  BarChartOutlined,
  BulbOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  CopyOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  LinkOutlined,
  LoadingOutlined,
  PaperClipOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SendOutlined,
  StopOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { history, useLocation } from '@umijs/max';
import { useSession, useTenant } from '@zhiyue/core';
import {
  Alert,
  Button,
  Drawer,
  Grid,
  Input,
  message,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import React from 'react';
import { chatWithFamsAiAssistant } from '@/services/bff/ai-assistant.api';
import {
  createManagerOnboardingDraft,
  getManagerOnboardingDraft,
  parseManagerOnboardingDraft,
  uploadManagerOnboardingFiles,
} from '@/services/bff/manager-onboarding.api';
import type {
  FamsAiActionProposal,
  FamsAiAssistantContext,
  FamsAiAssistantResponse,
  FamsAiMetric,
  FamsAiPageType,
  FamsAiToolResult,
} from '@/services/bff/types/ai-assistant';
import { AiActionReviewModal } from './AiActionReviewModal';
import './index.less';

const OPEN_EVENT = 'fams-ai-assistant-open';

export interface AiAssistantOpenDetail {
  prompt?: string;
}

type AiPageNameDetail = { route: string; name: string };

export const openAiAssistant = (prompt?: string) => {
  window.dispatchEvent(
    new CustomEvent<AiAssistantOpenDetail>(OPEN_EVENT, {
      detail: prompt?.trim() ? { prompt: prompt.trim() } : {},
    }),
  );
};

type ConversationMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  response?: FamsAiAssistantResponse;
};

type AssistantAttachment = {
  uuid: string;
  fileName: string;
  status: 'uploading' | 'ready' | 'failed';
  error?: string;
};

type AiAssistantDisplayContext = FamsAiAssistantContext & {
  investmentManagerName?: string;
};

const ASSISTANT_FILE_EXTENSIONS = new Set([
  '.pdf',
  '.docx',
  '.xlsx',
  '.pptx',
  '.jpg',
  '.jpeg',
  '.png',
]);
const ASSISTANT_FILE_MAX_SIZE = 50 * 1024 * 1024;

const attachmentIcon = (fileName: string) => {
  const extension = `.${fileName.split('.').pop()?.toLowerCase() || ''}`;
  if (extension === '.pdf') return <FilePdfOutlined />;
  if (['.jpg', '.jpeg', '.png'].includes(extension))
    return <FileImageOutlined />;
  return <FileTextOutlined />;
};

const pageTypeFromPath = (pathname: string): FamsAiPageType => {
  if (pathname === '/workbench/operations-overview') return 'workbench';
  if (pathname === '/manager-database/manager-management') return 'manager';
  if (
    pathname === '/risk-management/risk-alerts' ||
    pathname.includes('risk-alert')
  ) {
    return 'risk';
  }
  if (/^\/manager-database\/manager-management\/\d+$/.test(pathname)) {
    return 'manager';
  }
  if (/^\/manager-database\/my-investment-managers\/\d+$/.test(pathname)) {
    return 'investment_manager';
  }
  if (/^\/manager-database\/due-diligence\/\d+$/.test(pathname)) {
    return 'due_diligence';
  }
  if (/^\/portfolio-management\/product-management\/\d+$/.test(pathname)) {
    return 'product';
  }
  if (pathname.includes('/portfolio-') || pathname.includes('/hierarchy-')) {
    return 'portfolio';
  }
  return 'other';
};

const pageLabel = (context: AiAssistantDisplayContext) => {
  if (context.pageType === 'workbench') return '工作台';
  if (context.pageType === 'manager') {
    return context.managerId
      ? `管理人详情 #${context.managerId}`
      : '管理人列表';
  }
  if (context.pageType === 'risk') return '风险预警';
  if (context.pageType === 'investment_manager') {
    return context.investmentManagerId
      ? `投资经理详情 · ${context.investmentManagerName || `#${context.investmentManagerId}`}`
      : '投资经理研究';
  }
  if (context.pageType === 'due_diligence') return '尽调与准入';
  if (context.pageType === 'product') return '产品监控';
  if (context.pageType === 'portfolio') return '组合分析';
  return '当前页面';
};

const quickPrompts = (context: AiAssistantDisplayContext) => {
  if (
    context.pageType === 'investment_manager' &&
    context.investmentManagerId
  ) {
    return [
      { text: '总结当前投资经理', icon: <FileSearchOutlined /> },
      { text: '分析投资风格与方法', icon: <BarChartOutlined /> },
      { text: '梳理履历与能力圈', icon: <AuditOutlined /> },
      { text: '提炼尽调问题和资料缺口', icon: <WarningOutlined /> },
    ];
  }
  if (context.pageType === 'due_diligence') {
    return [
      { text: '总结当前尽调进展', icon: <AuditOutlined /> },
      { text: '列出尚未核实的问题', icon: <WarningOutlined /> },
      { text: '生成阶段性尽调结论草稿', icon: <FileSearchOutlined /> },
    ];
  }
  if (context.pageType === 'product') {
    return [
      { text: '解释当前产品表现', icon: <BarChartOutlined /> },
      { text: '检查估值和数据缺口', icon: <WarningOutlined /> },
      { text: '分析关联管理人与投资经理', icon: <FileSearchOutlined /> },
    ];
  }
  if (context.pageType === 'manager' && context.managerId) {
    return [
      { text: '总结当前管理人', icon: <FileSearchOutlined /> },
      { text: '有哪些信息缺失？', icon: <WarningOutlined /> },
      { text: '生成尽调问题', icon: <AuditOutlined /> },
      {
        text: '总结产品、策略、业绩和风险',
        icon: <BarChartOutlined />,
      },
    ];
  }
  if (context.pageType === 'manager') {
    return [
      { text: '查找资料不完整的管理人', icon: <FileSearchOutlined /> },
      { text: '哪些管理人风险最多？', icon: <WarningOutlined /> },
      { text: '对比重点管理人', icon: <BarChartOutlined /> },
      { text: '统计管理人类型与状态', icon: <DatabaseOutlined /> },
    ];
  }
  if (context.pageType === 'risk') {
    return [
      { text: '有多少高风险事项？', icon: <BarChartOutlined /> },
      { text: '列出仍在处理的风险', icon: <WarningOutlined /> },
      {
        text: '有哪些风险需要优先核查？',
        icon: <SafetyCertificateOutlined />,
      },
    ];
  }
  return [
    { text: '今天资产规模有什么变化？', icon: <BarChartOutlined /> },
    { text: '当前有哪些高风险事项？', icon: <WarningOutlined /> },
    { text: '有哪些配置偏离？', icon: <AuditOutlined /> },
    { text: '数据质量有哪些缺口？', icon: <DatabaseOutlined /> },
  ];
};

export const toAiRequestContext = (
  context: AiAssistantDisplayContext,
): FamsAiAssistantContext => {
  const requestContext = { ...context };
  delete requestContext.investmentManagerName;
  return requestContext;
};

const formatMetric = (metric: FamsAiMetric) => {
  if (metric.value === null || metric.value === undefined) return '--';
  if (typeof metric.value !== 'number') return metric.value;
  if (metric.format === 'amount') {
    return `${metric.value.toLocaleString('zh-CN', { maximumFractionDigits: 2 })} 元`;
  }
  if (metric.format === 'percent') return `${(metric.value * 100).toFixed(2)}%`;
  return metric.value.toLocaleString('zh-CN');
};

const readList = (result: FamsAiToolResult) => {
  const list = Array.isArray(result.data.list) ? result.data.list : [];
  if (result.name === 'search_managers') {
    return list.slice(0, 5).map((item) => {
      const row = item as Record<string, unknown>;
      return {
        key: String(row.id || row.managerCode || row.managerName),
        title: String(row.managerName || '未命名管理人'),
        detail: `产品 ${Number(row.productCount || 0)} · 风险 ${Number(row.openRiskCount || 0)}`,
      };
    });
  }
  if (result.name === 'list_risk_alerts') {
    return list.slice(0, 5).map((item) => {
      const row = item as Record<string, unknown>;
      return {
        key: String(row.key || row.alertCode),
        title: String(row.objectName || row.alertType || '风险预警'),
        detail: String(row.message || row.alertType || ''),
      };
    });
  }
  return [];
};

const ResultPreview: React.FC<{ results: FamsAiToolResult[] }> = ({
  results,
}) => {
  const rows = results.flatMap(readList);
  const managerResult = results.find(
    (item) => item.name === 'get_manager_summary',
  );
  const missingFields = Array.isArray(managerResult?.data.missingFields)
    ? (managerResult?.data.missingFields as string[])
    : [];
  const filters = results
    .map((item) => item.data.filters)
    .find((item) => item && typeof item === 'object') as
    | Record<string, unknown>
    | undefined;
  const filterLabels = [
    filters?.severity === 'HIGH'
      ? '高风险'
      : filters?.severity === 'MEDIUM'
        ? '中风险'
        : filters?.severity === 'LOW'
          ? '低风险'
          : '',
    filters?.status === 'OPEN'
      ? '待处置'
      : filters?.status === 'PROCESSING'
        ? '处理中'
        : filters?.status === 'CLOSED'
          ? '已关闭'
          : '',
    filters?.keyword ? `关键词：${String(filters.keyword)}` : '',
  ].filter(Boolean);
  if (!rows.length && !missingFields.length && !filterLabels.length)
    return null;
  return (
    <div className="fams-ai-result-preview">
      <Typography.Text strong>明细与核查项</Typography.Text>
      {filterLabels.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {filterLabels.map((label) => (
            <Tag key={label} color="blue">
              {label}
            </Tag>
          ))}
        </div>
      )}
      {missingFields.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {missingFields.map((field) => (
            <Tag key={field} color="warning">
              缺少{field}
            </Tag>
          ))}
        </div>
      )}
      {rows.map((row) => (
        <div key={row.key} className="fams-ai-result-row">
          <span>{row.title}</span>
          <Typography.Text type="secondary">{row.detail}</Typography.Text>
        </div>
      ))}
    </div>
  );
};

const Metrics: React.FC<{ metrics: FamsAiMetric[]; limit?: number }> = ({
  metrics,
  limit,
}) => {
  const visible = typeof limit === 'number' ? metrics.slice(0, limit) : metrics;
  if (!visible.length) return null;
  return (
    <div className="fams-ai-metrics">
      {visible.map((metric) => (
        <div className="fams-ai-metric" key={metric.id}>
          <span className="fams-ai-metric-label">{metric.label}</span>
          <span className="fams-ai-metric-value">{formatMetric(metric)}</span>
          <span className="fams-ai-metric-date">
            {metric.dataDate || '最新可用数据'} · {metric.source}
          </span>
        </div>
      ))}
    </div>
  );
};

const AssistantAnswer: React.FC<{
  response: FamsAiAssistantResponse;
  onAsk: (question: string) => void;
  onRegenerate?: () => void;
  onReviewAction?: (proposal: FamsAiActionProposal) => void;
}> = ({ response, onAsk, onRegenerate, onReviewAction }) => (
  <div className="fams-ai-assistant-message">
    <div className="fams-ai-message-avatar" aria-hidden="true">
      <BulbOutlined />
    </div>
    <div className="fams-ai-answer">
      <div className="fams-ai-answer-heading">
        <Space size={6}>
          <Typography.Text strong>AI 研究助手</Typography.Text>
          {response.fallback && <Tag color="orange">降级结果</Tag>}
        </Space>
        <Space size={2}>
          {onRegenerate && (
            <Tooltip title="重新生成">
              <Button
                type="text"
                size="small"
                aria-label="重新生成"
                icon={<ReloadOutlined />}
                onClick={onRegenerate}
              />
            </Tooltip>
          )}
          <Tooltip title="复制回答">
            <Button
              type="text"
              size="small"
              aria-label="复制回答"
              icon={<CopyOutlined />}
              onClick={() => {
                void navigator.clipboard.writeText(response.answer);
                message.success('回答已复制');
              }}
            />
          </Tooltip>
        </Space>
      </div>
      <Typography.Paragraph className="fams-ai-answer-text">
        {response.answer}
      </Typography.Paragraph>
      {response.warnings.map((warning) => (
        <Alert
          key={warning}
          type="warning"
          showIcon
          message={warning}
          className="fams-ai-answer-warning"
        />
      ))}
      <Metrics metrics={response.metrics} />
      <ResultPreview results={response.results} />
      {response.sources.length > 0 && (
        <div className="fams-ai-sources">
          <Typography.Text strong>数据来源</Typography.Text>
          <div className="fams-ai-source-list">
            {response.sources.map((source) => (
              <Button
                key={source.id}
                type="link"
                size="small"
                icon={<LinkOutlined />}
                onClick={() => history.push(source.route)}
              >
                {source.label} · {source.dataDate || '最新'}
              </Button>
            ))}
          </div>
        </div>
      )}
      {response.actions.length > 0 && (
        <div className="fams-ai-actions">
          <Space wrap>
            {response.actions.map((action) => (
              <Button
                key={`${action.route}-${action.label}`}
                onClick={() => history.push(action.route)}
              >
                {action.label}
              </Button>
            ))}
          </Space>
        </div>
      )}
      {(response.proposedActions || []).length > 0 && (
        <div className="fams-ai-proposed-actions">
          <Typography.Text strong>建议动作</Typography.Text>
          {(response.proposedActions || []).map((proposal) => (
            <div
              className="fams-ai-proposed-action"
              key={`${proposal.actionType}-${proposal.targetId}-${proposal.title}`}
            >
              <span>
                <AuditOutlined />
              </span>
              <div>
                <Typography.Text strong>{proposal.title}</Typography.Text>
                <Typography.Text
                  type="secondary"
                  ellipsis={{ tooltip: proposal.description }}
                >
                  {proposal.description}
                </Typography.Text>
              </div>
              <Button size="small" onClick={() => onReviewAction?.(proposal)}>
                审核
              </Button>
            </div>
          ))}
        </div>
      )}
      {response.suggestedQuestions.length > 0 && (
        <div className="fams-ai-follow-ups">
          <span className="fams-ai-section-label">继续追问</span>
          <div className="fams-ai-follow-up-list">
            {response.suggestedQuestions.map((question) => (
              <Button key={question} onClick={() => onAsk(question)}>
                {question}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

export const getAiPageContext = (
  pathname: string,
  search: string,
): FamsAiAssistantContext => {
  const pageType = pageTypeFromPath(pathname);
  const managerMatch = pathname.match(
    /^\/manager-database\/manager-management\/(\d+)$/,
  );
  const investmentManagerMatch = pathname.match(
    /^\/manager-database\/my-investment-managers\/(\d+)$/,
  );
  const dueDiligenceMatch = pathname.match(
    /^\/manager-database\/due-diligence\/(\d+)$/,
  );
  const productMatch = pathname.match(
    /^\/portfolio-management\/product-management\/(\d+)$/,
  );
  const searchParams = new URLSearchParams(search);
  const filters = Object.fromEntries(searchParams.entries());
  return {
    route: `${pathname}${search}`,
    pageType,
    managerId: managerMatch ? Number(managerMatch[1]) : undefined,
    investmentManagerId: investmentManagerMatch
      ? Number(investmentManagerMatch[1])
      : undefined,
    dueDiligenceId: dueDiligenceMatch
      ? Number(dueDiligenceMatch[1])
      : undefined,
    productId: productMatch
      ? Number(productMatch[1])
      : Number(searchParams.get('productId')) || undefined,
    hierarchyObjectKey: searchParams.get('objectKey') || undefined,
    riskAlertKey: searchParams.get('alertKey') || undefined,
    snapshotDate: searchParams.get('snapshotDate') || undefined,
    filters,
  };
};

export const buildAiSessionStorageKey = (tenantId: string, userId: string) =>
  `fams-ai-assistant:${tenantId}:${userId}`;

export const AiAssistantTrigger: React.FC = () => (
  <Tooltip title="AI 研究助手">
    <Button
      type="text"
      className="fams-ai-trigger"
      aria-label="打开 AI 研究助手"
      onClick={() => openAiAssistant()}
    >
      <span className="fams-ai-trigger-icon" aria-hidden="true">
        <BulbOutlined />
      </span>
    </Button>
  </Tooltip>
);

export const AiAssistantWorkspace: React.FC = () => {
  const location = useLocation();
  const screens = Grid.useBreakpoint();
  const { currentUser } = useSession();
  const { currentTenantUuid } = useTenant();
  const [open, setOpen] = React.useState(false);
  const [input, setInput] = React.useState('');
  const [messages, setMessages] = React.useState<ConversationMessage[]>([]);
  const [loadedStorageKey, setLoadedStorageKey] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [pendingPrompt, setPendingPrompt] = React.useState<string | null>(null);
  const [attachment, setAttachment] =
    React.useState<AssistantAttachment | null>(null);
  const [attachmentBusy, setAttachmentBusy] = React.useState(false);
  const [draggingFile, setDraggingFile] = React.useState(false);
  const [investmentManagerName, setInvestmentManagerName] =
    React.useState<string>();
  const [reviewingAction, setReviewingAction] =
    React.useState<FamsAiActionProposal | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);
  const messageEndRef = React.useRef<HTMLDivElement | null>(null);
  const context = React.useMemo(
    () => ({
      ...getAiPageContext(location.pathname, location.search),
      investmentManagerName,
    }),
    [investmentManagerName, location.pathname, location.search],
  );
  const currentUserUuid = (currentUser as { uuid?: string } | null)?.uuid;
  const userId = String(currentUser?.id || currentUserUuid || 'anonymous');
  const tenantId = currentTenantUuid || currentUser?.tenantUuid || 'no-tenant';
  const storageKey = buildAiSessionStorageKey(tenantId, userId);

  const attachFile = React.useCallback(async (file: File) => {
    const extension = `.${file.name.split('.').pop()?.toLowerCase() || ''}`;
    if (!ASSISTANT_FILE_EXTENSIONS.has(extension)) {
      message.error('仅支持 PDF、DOCX、XLSX、PPTX、JPG 和 PNG 文件');
      return;
    }
    if (file.size > ASSISTANT_FILE_MAX_SIZE) {
      message.error('单个文件不能超过 50 MB');
      return;
    }
    setAttachmentBusy(true);
    setAttachment({ uuid: '', fileName: file.name, status: 'uploading' });
    try {
      const draft = await createManagerOnboardingDraft('upload');
      await uploadManagerOnboardingFiles(draft.uuid, [file]);
      await parseManagerOnboardingDraft(draft.uuid);
      let current = await getManagerOnboardingDraft(draft.uuid);
      for (
        let attempt = 0;
        attempt < 120 && ['queued', 'parsing'].includes(current.status);
        attempt += 1
      ) {
        await new Promise((resolve) => window.setTimeout(resolve, 1000));
        current = await getManagerOnboardingDraft(draft.uuid);
      }
      if (current.status !== 'parsed') {
        const raw = current.errorMessage || '材料解析失败，请稍后重试';
        throw new Error(
          /timeout/i.test(raw)
            ? '文件较大，解析超时。长文档会先保留原文，请再试一次或拆成更小的文件。'
            : raw,
        );
      }
      setAttachment({ uuid: draft.uuid, fileName: file.name, status: 'ready' });
      message.success('材料解析完成，可以开始提问');
    } catch (error) {
      setAttachment({
        uuid: '',
        fileName: file.name,
        status: 'failed',
        error: error instanceof Error ? error.message : '材料解析失败',
      });
      message.error(error instanceof Error ? error.message : '材料解析失败');
    } finally {
      setAttachmentBusy(false);
    }
  }, []);

  React.useEffect(() => {
    setInvestmentManagerName(undefined);
  }, [location.pathname]);

  React.useEffect(() => {
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<AiPageNameDetail>).detail;
      if (detail?.route === location.pathname && detail.name) {
        setInvestmentManagerName(detail.name);
      }
    };
    window.addEventListener('fams-ai-page-name', listener);
    return () => window.removeEventListener('fams-ai-page-name', listener);
  }, [location.pathname]);

  React.useEffect(() => {
    const listener = (event: Event) => {
      const prompt = (event as CustomEvent<AiAssistantOpenDetail>).detail
        ?.prompt;
      setOpen(true);
      if (prompt?.trim()) setPendingPrompt(prompt.trim());
    };
    window.addEventListener(OPEN_EVENT, listener);
    return () => window.removeEventListener(OPEN_EVENT, listener);
  }, []);

  React.useEffect(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      setMessages(stored ? (JSON.parse(stored) as ConversationMessage[]) : []);
    } catch {
      setMessages([]);
    }
    setLoadedStorageKey(storageKey);
  }, [storageKey]);

  React.useEffect(() => {
    if (loadedStorageKey !== storageKey) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(messages.slice(-30)));
    } catch {
      // Keep the active conversation usable when browser storage is full.
    }
  }, [loadedStorageKey, messages, storageKey]);

  React.useEffect(() => {
    if (typeof messageEndRef.current?.scrollIntoView === 'function') {
      messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  const ask = React.useCallback(
    async (question: string) => {
      const text = question.trim();
      if (!text || loading) return;
      const userMessage: ConversationMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: text,
      };
      setMessages((current) => [...current, userMessage]);
      setInput('');
      setLoading(true);
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const response = await chatWithFamsAiAssistant(
          {
            message: text,
            context: {
              ...toAiRequestContext(context),
              attachmentDraftUuid:
                attachment?.status === 'ready' ? attachment.uuid : undefined,
            },
            history: messages.slice(-16).map((item) => ({
              role: item.role,
              content: item.content,
            })),
          },
          { signal: controller.signal },
        );
        setMessages((current) => [
          ...current,
          {
            id: `assistant-${response.requestId}`,
            role: 'assistant',
            content: response.answer,
            response,
          },
        ]);
      } catch (error) {
        if (!controller.signal.aborted) {
          message.error(
            error instanceof Error ? error.message : 'AI 助手请求失败',
          );
        }
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        setLoading(false);
      }
    },
    [attachment, context, loading, messages],
  );

  React.useEffect(() => {
    if (!pendingPrompt || loading || loadedStorageKey !== storageKey) {
      return;
    }
    const prompt = pendingPrompt;
    setPendingPrompt(null);
    void ask(prompt);
  }, [ask, loadedStorageKey, loading, pendingPrompt, storageKey]);

  const downloadDraft = () => {
    const markdown = messages
      .map(
        (item) =>
          `${item.role === 'user' ? '## 用户' : '## AI 助手'}\n\n${item.content}`,
      )
      .join('\n\n');
    const url = URL.createObjectURL(
      new Blob([markdown], { type: 'text/markdown;charset=utf-8' }),
    );
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `FAMS-AI-工作草稿-${new Date().toISOString().slice(0, 10)}.md`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Drawer
      className="fams-ai-drawer"
      title={
        <div className="fams-ai-drawer-title">
          <span className="fams-ai-drawer-mark" aria-hidden="true">
            <BulbOutlined />
          </span>
          <span className="fams-ai-drawer-title-copy">
            <strong>AI 研究助手</strong>
            <span>
              <i /> 已连接业务数据
            </span>
          </span>
        </div>
      }
      width={screens.md ? 560 : '100%'}
      open={open}
      onClose={() => setOpen(false)}
      extra={
        <Space size={2}>
          <Tooltip title="下载当前工作草稿">
            <Button
              type="text"
              aria-label="下载当前工作草稿"
              icon={<DownloadOutlined />}
              disabled={!messages.length}
              onClick={downloadDraft}
            />
          </Tooltip>
          <Popconfirm
            title="清空当前会话？"
            description="已生成的对话内容将从本次浏览器会话中移除。"
            okText="清空"
            cancelText="取消"
            okButtonProps={{ danger: true }}
            disabled={!messages.length}
            onConfirm={() => setMessages([])}
          >
            <Tooltip title="清空当前会话">
              <Button
                type="text"
                aria-label="清空当前会话"
                icon={<DeleteOutlined />}
                disabled={!messages.length}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      }
    >
      <div className="fams-ai-context">
        <div className="fams-ai-context-main">
          <FileSearchOutlined />
          <span>
            <small>当前页面</small>
            <strong className="fams-ai-context-label">
              {pageLabel(context)}
            </strong>
          </span>
        </div>
        <div className="fams-ai-context-source">
          <DatabaseOutlined />
          <span>正式数据</span>
          {context.snapshotDate && <Tag>{context.snapshotDate}</Tag>}
        </div>
      </div>
      <div className="fams-ai-messages">
        {!messages.length && !loading ? (
          <div className="fams-ai-empty">
            <div className="fams-ai-empty-mark" aria-hidden="true">
              <BulbOutlined />
            </div>
            <Typography.Title level={4}>可以开始提问</Typography.Title>
            <Typography.Text type="secondary">
              结合当前「{pageLabel(context)}」页面数据，或上传
              PDF、图片等资料解读。
            </Typography.Text>
            <Upload
              accept=".pdf,.docx,.xlsx,.pptx,.jpg,.jpeg,.png"
              showUploadList={false}
              beforeUpload={(file) => {
                void attachFile(file as File);
                return Upload.LIST_IGNORE;
              }}
              disabled={loading || attachmentBusy}
            >
              <button
                type="button"
                className="fams-ai-empty-upload"
                disabled={loading || attachmentBusy}
              >
                <CloudUploadOutlined />
                <div>
                  <strong>上传资料后再问</strong>
                  <span>
                    营业执照、路演材料、PDF / 图片 / Office，单文件不超过 50MB
                  </span>
                </div>
              </button>
            </Upload>
            <div className="fams-ai-starters">
              <span className="fams-ai-section-label">建议问题</span>
              {quickPrompts(context).map((prompt) => (
                <Button key={prompt.text} onClick={() => void ask(prompt.text)}>
                  <span className="fams-ai-starter-icon">{prompt.icon}</span>
                  <span>{prompt.text}</span>
                  <ArrowRightOutlined />
                </Button>
              ))}
            </div>
          </div>
        ) : null}
        {messages.map((item, index) => {
          const previousQuestion = messages
            .slice(0, index)
            .reverse()
            .find((messageItem) => messageItem.role === 'user')?.content;
          return (
            <div
              key={item.id}
              className={`fams-ai-message fams-ai-message-${item.role}`}
            >
              {item.role === 'user' ? (
                <div className="fams-ai-user-bubble">{item.content}</div>
              ) : item.response ? (
                <AssistantAnswer
                  response={item.response}
                  onAsk={(question) => void ask(question)}
                  onRegenerate={
                    previousQuestion
                      ? () => void ask(previousQuestion)
                      : undefined
                  }
                  onReviewAction={setReviewingAction}
                />
              ) : null}
            </div>
          );
        })}
        {loading && (
          <div className="fams-ai-assistant-message">
            <div className="fams-ai-message-avatar" aria-hidden="true">
              <BulbOutlined />
            </div>
            <div className="fams-ai-loading-state">
              <Spin indicator={<LoadingOutlined spin />} />
              <Typography.Text>正在查询并核对业务数据...</Typography.Text>
            </div>
          </div>
        )}
        <div ref={messageEndRef} />
      </div>
      <div className="fams-ai-composer">
        <div
          className={`fams-ai-composer-box${draggingFile ? ' is-dragging' : ''}`}
          onDragEnter={(event) => {
            event.preventDefault();
            if (event.dataTransfer.types.includes('Files'))
              setDraggingFile(true);
          }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node)) {
              setDraggingFile(false);
            }
          }}
          onDrop={(event) => {
            event.preventDefault();
            setDraggingFile(false);
            const file = event.dataTransfer.files?.[0];
            if (file) void attachFile(file);
          }}
        >
          {draggingFile ? (
            <div className="fams-ai-drop-hint" aria-hidden="true">
              <CloudUploadOutlined />
              <span>松开即可上传资料</span>
            </div>
          ) : null}
          {attachment && (
            <div className={`fams-ai-attachment is-${attachment.status}`}>
              <span className="fams-ai-attachment-icon">
                {attachmentIcon(attachment.fileName)}
              </span>
              <span className="fams-ai-attachment-copy">
                <strong title={attachment.fileName}>
                  {attachment.fileName}
                </strong>
                <small>
                  {attachment.status === 'uploading'
                    ? '正在解析…'
                    : attachment.status === 'failed'
                      ? attachment.error || '解析失败'
                      : '已解析，可以提问'}
                </small>
              </span>
              {attachment.status === 'uploading' && <Spin size="small" />}
              <Tooltip title="移除附件" placement="top">
                <Button
                  type="text"
                  size="small"
                  aria-label="移除附件"
                  icon={<CloseOutlined />}
                  onClick={() => setAttachment(null)}
                />
              </Tooltip>
            </div>
          )}
          <Input.TextArea
            value={input}
            variant="borderless"
            maxLength={2000}
            rows={3}
            placeholder={
              attachment?.status === 'ready'
                ? '针对这份资料提问，例如统一社会信用代码是多少？'
                : '输入问题，或把 PDF、图片拖到这里'
            }
            onChange={(event) => setInput(event.target.value)}
            onPressEnter={(event) => {
              if (!event.shiftKey) {
                event.preventDefault();
                void ask(input);
              }
            }}
          />
          <div className="fams-ai-composer-actions">
            <div className="fams-ai-composer-tools">
              <span className="fams-ai-composer-badge">
                <DatabaseOutlined /> 正式数据
              </span>
              <Upload
                accept=".pdf,.docx,.xlsx,.pptx,.jpg,.jpeg,.png"
                showUploadList={false}
                beforeUpload={(file) => {
                  void attachFile(file as File);
                  return Upload.LIST_IGNORE;
                }}
                disabled={loading || attachmentBusy}
              >
                <Button
                  type="text"
                  className="fams-ai-upload-btn"
                  aria-label="上传资料"
                  icon={<PaperClipOutlined />}
                  disabled={loading || attachmentBusy}
                >
                  上传资料
                </Button>
              </Upload>
            </div>
            {loading ? (
              <Button
                aria-label="停止生成"
                icon={<StopOutlined />}
                onClick={() => abortRef.current?.abort()}
              >
                停止
              </Button>
            ) : (
              <Button
                type="primary"
                className="fams-ai-send-btn"
                aria-label="发送"
                icon={<SendOutlined />}
                disabled={!input.trim() && attachment?.status !== 'ready'}
                onClick={() => void ask(input || '请解读这份资料')}
              >
                发送
              </Button>
            )}
          </div>
        </div>
        <div className="fams-ai-composer-hint">
          支持 PDF、图片、Word / Excel / PPT，单个不超过 50MB
        </div>
      </div>
      <AiActionReviewModal
        open={Boolean(reviewingAction)}
        proposal={reviewingAction}
        onClose={() => setReviewingAction(null)}
      />
    </Drawer>
  );
};

export const AiWorkbenchHighlights: React.FC<{
  snapshotDate?: string | null;
}> = ({ snapshotDate }) => {
  const { currentTenantUuid } = useTenant();
  const [response, setResponse] =
    React.useState<FamsAiAssistantResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const cacheKey = `fams-ai-highlights:${currentTenantUuid || 'no-tenant'}:${snapshotDate || 'latest'}`;

  const load = React.useCallback(
    async (refresh = false) => {
      if (!refresh) {
        try {
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) {
            setResponse(JSON.parse(cached) as FamsAiAssistantResponse);
            return;
          }
        } catch {
          // Ignore invalid session cache and refresh from the service.
        }
      }
      setLoading(true);
      try {
        const next = await chatWithFamsAiAssistant({
          message:
            '请总结当前估值日最需要关注的资产变化、配置偏离、高风险事项和数据缺口。',
          context: {
            route: '/workbench/operations-overview',
            pageType: 'workbench',
            snapshotDate: snapshotDate || undefined,
          },
        });
        setResponse(next);
        sessionStorage.setItem(cacheKey, JSON.stringify(next));
      } catch {
        setResponse(null);
      } finally {
        setLoading(false);
      }
    },
    [cacheKey, snapshotDate],
  );

  React.useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="fams-ai-highlights" aria-label="今日智能重点">
      <div className="fams-ai-highlights-header">
        <div>
          <div className="fams-ai-highlights-title">
            <BulbOutlined /> 今日智能重点
          </div>
          <Typography.Text type="secondary">
            {snapshotDate ? `数据日期 ${snapshotDate}` : '使用最新可用业务数据'}
          </Typography.Text>
        </div>
        <Button size="small" loading={loading} onClick={() => void load(true)}>
          刷新分析
        </Button>
      </div>
      {loading && !response ? (
        <Space>
          <Spin size="small" />
          <Typography.Text>正在核对工作台数据...</Typography.Text>
        </Space>
      ) : response ? (
        <>
          <Typography.Paragraph className="fams-ai-highlights-summary">
            {response.answer}
          </Typography.Paragraph>
          <div className="fams-ai-highlights-metrics">
            {response.metrics.slice(0, 5).map((metric) => (
              <div className="fams-ai-metric" key={metric.id}>
                <span className="fams-ai-metric-label">{metric.label}</span>
                <span className="fams-ai-metric-value">
                  {formatMetric(metric)}
                </span>
                <span className="fams-ai-metric-date">{metric.source}</span>
              </div>
            ))}
          </div>
          <Space wrap style={{ marginTop: 12 }}>
            {response.actions.map((action) => (
              <Button
                key={action.route}
                size="small"
                onClick={() => history.push(action.route)}
              >
                {action.label}
              </Button>
            ))}
          </Space>
        </>
      ) : (
        <Alert
          type="info"
          showIcon
          message="智能摘要暂不可用，原工作台数据不受影响。"
        />
      )}
    </section>
  );
};

const managerSummaryPrompts = [
  { label: '检查资料缺口', prompt: '当前管理人有哪些信息缺失？' },
  { label: '生成尽调问题', prompt: '请基于当前管理人资料生成尽调问题。' },
  {
    label: '综合分析',
    prompt: '请综合分析当前管理人的产品、策略、业绩和风险。',
  },
];

const managerMetricPriority = [
  'completeness',
  'risks',
  'managementAum',
  'products',
  'team',
];

const managerSummaryMetrics = (metrics: FamsAiMetric[]) =>
  [...metrics]
    .sort((left, right) => {
      const leftKey = left.id.split('.').at(-1) || '';
      const rightKey = right.id.split('.').at(-1) || '';
      const leftPriority = managerMetricPriority.indexOf(leftKey);
      const rightPriority = managerMetricPriority.indexOf(rightKey);
      return (
        (leftPriority < 0 ? managerMetricPriority.length : leftPriority) -
        (rightPriority < 0 ? managerMetricPriority.length : rightPriority)
      );
    })
    .slice(0, 4);

export const buildAiManagerSummaryStorageKey = (
  tenantId: string,
  userId: string,
  managerId: number,
  managerUpdatedAt?: string | null,
) =>
  `fams-ai-manager-summary:${tenantId}:${userId}:${managerId}:${managerUpdatedAt || 'unknown'}`;

export const AiManagerSummary: React.FC<{
  managerId: number;
  managerName: string;
  managerUpdatedAt?: string | null;
}> = ({ managerId, managerName, managerUpdatedAt }) => {
  const { currentUser } = useSession();
  const { currentTenantUuid } = useTenant();
  const [response, setResponse] =
    React.useState<FamsAiAssistantResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState(false);
  const requestSequence = React.useRef(0);
  const sessionUser = currentUser as {
    id?: string | number;
    memberUuid?: string;
    uuid?: string;
    tenantUuid?: string;
  } | null;
  const tenantId = currentTenantUuid || sessionUser?.tenantUuid || 'no-tenant';
  const userId = String(
    sessionUser?.memberUuid ||
      sessionUser?.id ||
      sessionUser?.uuid ||
      'anonymous',
  );
  const cacheKey = buildAiManagerSummaryStorageKey(
    tenantId,
    userId,
    managerId,
    managerUpdatedAt,
  );

  const load = React.useCallback(
    async (refresh = false) => {
      const requestId = requestSequence.current + 1;
      requestSequence.current = requestId;
      setLoadError(false);
      if (!refresh) {
        try {
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) {
            setResponse(JSON.parse(cached) as FamsAiAssistantResponse);
            return;
          }
        } catch {
          // Ignore invalid session cache and request a fresh summary.
        }
      }
      setLoading(true);
      try {
        const next = await chatWithFamsAiAssistant({
          message:
            '请总结当前管理人的主档、团队、策略、产品、业绩、风险、资料完整度和待补信息。',
          context: {
            route: `/manager-database/manager-management/${managerId}`,
            pageType: 'manager',
            managerId,
          },
        });
        if (requestSequence.current !== requestId) return;
        setResponse(next);
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(next));
        } catch {
          // A full browser storage area must not turn a successful summary into an error.
        }
      } catch {
        if (requestSequence.current !== requestId) return;
        setLoadError(true);
      } finally {
        if (requestSequence.current === requestId) setLoading(false);
      }
    },
    [cacheKey, managerId],
  );

  React.useEffect(() => {
    setResponse(null);
    void load();
    return () => {
      requestSequence.current += 1;
    };
  }, [load]);

  const dataDates = response
    ? Array.from(
        new Set(
          [...response.metrics, ...response.sources]
            .map((item) => item.dataDate)
            .filter((item): item is string => Boolean(item)),
        ),
      )
    : [];
  const visibleMetrics = response
    ? managerSummaryMetrics(response.metrics)
    : [];

  return (
    <section
      className="fams-ai-manager-summary"
      aria-label={`${managerName} AI 研究摘要`}
    >
      <div className="fams-ai-highlights-header">
        <div>
          <div className="fams-ai-highlights-title">
            <BulbOutlined /> AI 研究摘要
            {response?.fallback && <Tag color="orange">降级摘要</Tag>}
          </div>
          <Typography.Text type="secondary">
            {dataDates.length
              ? `数据日期 ${dataDates.join('、')}`
              : response
                ? `生成时间 ${new Date(response.generatedAt).toLocaleString('zh-CN')}`
                : '正在核对管理人正式数据'}
          </Typography.Text>
        </div>
        <Button size="small" loading={loading} onClick={() => void load(true)}>
          刷新分析
        </Button>
      </div>

      {loading && !response ? (
        <Space>
          <Spin size="small" />
          <Typography.Text>正在汇总管理人数据...</Typography.Text>
        </Space>
      ) : response ? (
        <>
          <Typography.Paragraph className="fams-ai-highlights-summary">
            {response.answer}
          </Typography.Paragraph>
          {visibleMetrics.length > 0 && (
            <div className="fams-ai-manager-metrics">
              {visibleMetrics.map((metric) => (
                <div className="fams-ai-metric" key={metric.id}>
                  <span className="fams-ai-metric-label">{metric.label}</span>
                  <span className="fams-ai-metric-value">
                    {formatMetric(metric)}
                  </span>
                  <span className="fams-ai-metric-date">
                    {metric.source}
                    {metric.dataDate ? ` · ${metric.dataDate}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
          {response.sources.length > 0 && (
            <Typography.Text
              type="secondary"
              className="fams-ai-manager-sources"
            >
              来源：{response.sources.map((source) => source.label).join('、')}
            </Typography.Text>
          )}
          {response.warnings.length > 0 && (
            <Alert
              className="fams-ai-manager-warning"
              type="warning"
              showIcon
              message={response.warnings.join('；')}
            />
          )}
          {loadError && (
            <Alert
              className="fams-ai-manager-warning"
              type="warning"
              showIcon
              message="刷新失败，当前展示上次生成的摘要。"
            />
          )}
        </>
      ) : (
        <Alert
          type="info"
          showIcon
          message="AI 摘要暂不可用，管理人原有数据和操作不受影响。"
        />
      )}

      <div className="fams-ai-manager-actions">
        {managerSummaryPrompts.map((item) => (
          <Button
            key={item.label}
            size="small"
            onClick={() => openAiAssistant(item.prompt)}
          >
            {item.label}
          </Button>
        ))}
        <Button size="small" type="link" onClick={() => openAiAssistant()}>
          打开 AI 助手继续追问
        </Button>
      </div>
    </section>
  );
};

const investmentManagerPrompts = [
  { label: '投资方法', prompt: '请分析当前投资经理的投资风格与方法。' },
  {
    label: '履历与能力圈',
    prompt: '请梳理当前投资经理的履历、市场经历与能力圈证据。',
  },
  { label: '资料缺口', prompt: '请提炼当前投资经理的尽调问题和资料缺口。' },
];

export const AiInvestmentManagerSummary: React.FC<{
  investmentManagerId: number;
  investmentManagerName: string;
  updatedAt?: string | null;
}> = ({ investmentManagerId, investmentManagerName, updatedAt }) => {
  const { currentUser } = useSession();
  const { currentTenantUuid } = useTenant();
  const [response, setResponse] =
    React.useState<FamsAiAssistantResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const sessionUser = currentUser as {
    id?: string | number;
    uuid?: string;
    tenantUuid?: string;
  } | null;
  const cacheKey = `fams-ai-investment-manager:${currentTenantUuid || sessionUser?.tenantUuid || 'no-tenant'}:${sessionUser?.id || sessionUser?.uuid || 'anonymous'}:${investmentManagerId}:${updatedAt || 'unknown'}`;

  const load = React.useCallback(
    async (refresh = false) => {
      if (!refresh) {
        try {
          const cached = sessionStorage.getItem(cacheKey);
          if (cached) {
            setResponse(JSON.parse(cached) as FamsAiAssistantResponse);
            return;
          }
        } catch {
          // Refresh when the cache is unavailable.
        }
      }
      setLoading(true);
      try {
        const next = await chatWithFamsAiAssistant({
          message:
            '请基于当前投资经理的档案、履历、投资画像、能力圈、关联产品、业绩证据、内部笔记和评价，给出人物研究摘要并列出资料缺口。',
          context: {
            route: `/manager-database/my-investment-managers/${investmentManagerId}`,
            pageType: 'investment_manager',
            investmentManagerId,
          },
        });
        setResponse(next);
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(next));
        } catch {
          // Keep a successful response even when browser storage is full.
        }
      } catch {
        setResponse(null);
      } finally {
        setLoading(false);
      }
    },
    [cacheKey, investmentManagerId],
  );

  React.useEffect(() => {
    void load();
  }, [load]);

  return (
    <section
      className="fams-ai-investment-manager-summary"
      aria-label={`${investmentManagerName} AI 人物研究摘要`}
    >
      <div className="fams-ai-highlights-header">
        <div>
          <div className="fams-ai-highlights-title">
            <FileSearchOutlined /> AI 人物研究
            {response?.fallback && <Tag color="orange">确定性摘要</Tag>}
          </div>
          <Typography.Text type="secondary">
            读取人物档案、履历、投资画像和研究证据，不使用工作台统计替代人物分析
          </Typography.Text>
        </div>
        <Button size="small" loading={loading} onClick={() => void load(true)}>
          刷新分析
        </Button>
      </div>
      {loading && !response ? (
        <Space>
          <Spin size="small" />
          <Typography.Text>正在核对人物研究资料...</Typography.Text>
        </Space>
      ) : response ? (
        <>
          <Typography.Paragraph className="fams-ai-highlights-summary">
            {response.answer}
          </Typography.Paragraph>
          <Metrics metrics={response.metrics} limit={6} />
          {response.warnings.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={response.warnings.join('；')}
              className="fams-ai-manager-warning"
            />
          )}
        </>
      ) : (
        <Alert
          type="info"
          showIcon
          message="人物研究摘要暂不可用，原投资经理数据不受影响。"
        />
      )}
      <div className="fams-ai-manager-actions">
        {investmentManagerPrompts.map((item) => (
          <Button
            key={item.label}
            size="small"
            onClick={() => openAiAssistant(item.prompt)}
          >
            {item.label}
          </Button>
        ))}
        <Button size="small" type="link" onClick={() => openAiAssistant()}>
          继续追问
        </Button>
      </div>
    </section>
  );
};
